/**
 * Operator profile page — card spotlight-056.
 *
 * Lets the signed-in user manage how they present themselves
 * (display name, avatar, signature, timezone, locale) and revoke
 * every active session via "Sign out everywhere".
 *
 * Save is an explicit primary action — no surprise auto-saves.  The
 * Email field is read-only (the identity provider owns it).  The
 * avatar upload uses the same drag-target pattern as the help
 * attachments composer.
 *
 * No vendor names in copy — anywhere a user-facing string would
 * have read "Clerk" or "Cloudflare", we say "your sign-in provider"
 * or "storage" instead.  See card spotlight-052.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth, useClerk } from '@clerk/clerk-react';

import {
  createApiClient,
  fetchMyProfile,
  fetchVapidPublicKey,
  listMyPushSubscriptions,
  operatorSubscribePush,
  operatorUnsubscribePush,
  sendPushTest,
  setPushDoNotDisturb,
  signedAvatarSrc,
  updateMyProfile,
  uploadAvatar,
  vapidKeyToUint8Array,
} from '../api.js';
import { useConfirm } from '../components/ConfirmDialog.jsx';
import { useToast } from '../components/Toast.jsx';
import {
  SaveIcon,
  UploadIcon,
  RefreshIcon,
  DisableIcon,
} from '../components/icons';

const TIMEZONES = (() => {
  // Browser-supplied IANA list when the platform supports it; fall
  // back to a hand-picked dozen so the picker is never empty.
  try {
    if (typeof Intl !== 'undefined' && typeof Intl.supportedValuesOf === 'function') {
      const all = Intl.supportedValuesOf('timeZone');
      if (Array.isArray(all) && all.length > 0) return all;
    }
  } catch (err) {
    // ignore — fall through to fallback list
  }
  return [
    'UTC',
    'Europe/London',
    'Europe/Paris',
    'Europe/Berlin',
    'America/New_York',
    'America/Chicago',
    'America/Los_Angeles',
    'America/Sao_Paulo',
    'Asia/Tokyo',
    'Asia/Singapore',
    'Asia/Kolkata',
    'Australia/Sydney',
  ];
})();

/** Best-effort browser × OS label from a UA string.  Doesn't try
 *  to be exhaustive — just enough so the device list row reads
 *  "Chrome on macOS" instead of a 200-char UA blob.
 */
function parseUserAgent(ua) {
  if (!ua) return 'Unknown device';
  const u = ua.toLowerCase();
  let browser = 'Browser';
  if (u.includes('edg/')) browser = 'Edge';
  else if (u.includes('firefox')) browser = 'Firefox';
  else if (u.includes('chrome')) browser = 'Chrome';
  else if (u.includes('safari')) browser = 'Safari';
  let os = 'unknown OS';
  if (u.includes('mac os')) os = 'macOS';
  else if (u.includes('windows')) os = 'Windows';
  else if (u.includes('android')) os = 'Android';
  else if (u.includes('iphone') || u.includes('ipad')) os = 'iOS';
  else if (u.includes('linux')) os = 'Linux';
  return `${browser} on ${os}`;
}

const LOCALES = [
  { code: '', label: 'Use my browser language' },
  { code: 'en', label: 'English' },
  { code: 'en-GB', label: 'English (United Kingdom)' },
  { code: 'en-US', label: 'English (United States)' },
  { code: 'es', label: 'Spanish' },
  { code: 'fr', label: 'French' },
  { code: 'de', label: 'German' },
  { code: 'pt-BR', label: 'Portuguese (Brazil)' },
  { code: 'ja', label: 'Japanese' },
  { code: 'zh-Hans', label: 'Chinese (Simplified)' },
];

// Field caps mirror the backend entity — keeping them in sync so
// the user sees the limit at the textarea rather than as a 422.
const DISPLAY_NAME_MAX = 80;
const SIGNATURE_MAX = 500;
const AVATAR_MAX_BYTES = 2 * 1024 * 1024;
const AVATAR_MIMES = new Set(['image/png', 'image/jpeg', 'image/gif', 'image/webp']);

export function Profile() {
  const { getToken } = useAuth();
  const clerk = useClerk();
  const { showToast } = useToast();
  const [confirmNode, askConfirm] = useConfirm();

  const [profile, setProfile] = useState(null);
  const [original, setOriginal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const dropTargetRef = useRef(null);
  const fileInputRef = useRef(null);

  // ── push-notification state (card spotlight-047) ──────────
  // ``pushSupported`` covers the legacy-browser case (Safari < 16,
  // some embedded webviews) where ``serviceWorker`` or
  // ``PushManager`` is missing — we show a "not supported" stub
  // instead of a broken toggle.
  const pushSupported =
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window;
  const [pushPermission, setPushPermission] = useState(
    pushSupported ? Notification.permission : 'denied'
  );
  const [pushSubs, setPushSubs] = useState([]);
  const [pushBusy, setPushBusy] = useState(false);
  const [pushTestBusy, setPushTestBusy] = useState(false);
  // ``pushError`` is rendered inline beneath the Re-enable button.
  // Toasts disappear after a few seconds and the user reported "I
  // clicked and nothing happened" (card spotlight-062) — surfacing
  // the actual error message next to the button means the failure
  // can't be missed.
  const [pushError, setPushError] = useState(null);
  // VAPID public key is fetched lazily on mount.  If the backend
  // hasn't been configured (empty SSM param, 5xx, network error)
  // we surface a yellow callout in the card itself and disable
  // the enable button, instead of letting the click silently
  // fail with a "Subscription failed" deep inside pushManager.
  const [vapidStatus, setVapidStatus] = useState({
    key: null,
    error: null,
    checked: false,
  });
  const [localTestBusy, setLocalTestBusy] = useState(false);

  const adminApi = useMemo(() => createApiClient(getToken), [getToken]);

  const isDirty = useMemo(() => {
    if (!profile || !original) return false;
    return (
      profile.display_name !== original.display_name ||
      profile.signature !== original.signature ||
      profile.timezone !== original.timezone ||
      profile.locale !== original.locale ||
      profile.pinpoint_visible !== original.pinpoint_visible
    );
  }, [profile, original]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchMyProfile(getToken);
      setProfile(data);
      setOriginal(data);
    } catch (err) {
      setError(err?.message || 'We couldn’t load your profile.');
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  useEffect(() => {
    load();
  }, [load]);

  // Pull the device list whenever we land on the page so a brand-
  // new tab shows the same state another tab might've just
  // unsubscribed.  Errors are swallowed — the device list is a
  // nice-to-have, not a blocking surface.
  const refreshPushSubs = useCallback(async () => {
    if (!pushSupported) return;
    try {
      const subs = await listMyPushSubscriptions(adminApi);
      setPushSubs(subs);
    } catch {
      setPushSubs([]);
    }
  }, [adminApi, pushSupported]);

  useEffect(() => {
    refreshPushSubs();
  }, [refreshPushSubs]);

  // Probe the VAPID public-key endpoint once at mount.  This lets
  // us render a deploy-gap callout BEFORE the user clicks Enable
  // — much better UX than waiting for the click to throw deep
  // inside ``pushManager.subscribe``.  Also caches the key so the
  // happy-path click doesn't pay an extra round-trip.
  useEffect(() => {
    if (!pushSupported) return undefined;
    let alive = true;
    (async () => {
      try {
        const key = await fetchVapidPublicKey();
        if (!alive) return;
        if (!key) {
          setVapidStatus({
            key: null,
            error:
              "Push isn't configured on this deployment — VAPID public key is empty.",
            checked: true,
          });
          return;
        }
        // eslint-disable-next-line no-console
        console.log('[push] VAPID public key fetched, length:', key.length);
        setVapidStatus({ key, error: null, checked: true });
      } catch (err) {
        if (!alive) return;
        // eslint-disable-next-line no-console
        console.warn('[push] VAPID public-key endpoint failed', err);
        setVapidStatus({
          key: null,
          error:
            err?.message ||
            "Couldn't reach the push configuration endpoint.",
          checked: true,
        });
      }
    })();
    return () => {
      alive = false;
    };
  }, [pushSupported]);

  const handleEnablePush = useCallback(async () => {
    if (!pushSupported) return;
    setPushBusy(true);
    setPushError(null);
    try {
      // Request permission first — the OS prompt is the part the
      // user has to engage with; everything else is plumbing.
      let permission = Notification.permission;
      if (permission === 'default') {
        permission = await Notification.requestPermission();
      }
      setPushPermission(permission);
      if (permission !== 'granted') {
        const msg =
          permission === 'denied'
            ? 'Notifications are blocked in your browser settings. Enable them there to opt in.'
            : 'Notifications need permission before we can send them.';
        setPushError(msg);
        showToast({
          title: 'Permission needed',
          body: msg,
          variant: 'info',
        });
        return;
      }
      // Then mint the subscription.  ``ready`` resolves once the
      // SW from card 046 is controlling the page; without that
      // ``pushManager`` would 503.
      const reg = await navigator.serviceWorker.ready;
      // eslint-disable-next-line no-console
      console.log('[push] service worker ready, scope:', reg.scope);
      let vapidKey = vapidStatus.key;
      if (!vapidKey) {
        // Fall back to a live fetch if the mount-time check
        // hasn't completed (or failed and the user is retrying).
        vapidKey = await fetchVapidPublicKey();
      }
      if (!vapidKey) {
        throw new Error(
          'Push is not configured on this deployment yet — ask an admin to set the VAPID keys in SSM.'
        );
      }
      const existing = await reg.pushManager.getSubscription();
      if (existing) {
        // eslint-disable-next-line no-console
        console.log('[push] dropping stale browser subscription');
        // Drop any old browser-side sub so we don't end up with
        // multiple `endpoint`s for the same install.
        try {
          await existing.unsubscribe();
        } catch {
          // browser-side cleanup is best-effort
        }
      }
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: vapidKeyToUint8Array(vapidKey),
      });
      const json = sub.toJSON();
      // eslint-disable-next-line no-console
      console.log('[push] subscribed, endpoint host:', new URL(json.endpoint).host);
      await operatorSubscribePush(adminApi, {
        endpoint: json.endpoint,
        keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
        project_id: '',
      });
      // eslint-disable-next-line no-console
      console.log('[push] backend accepted subscription');
      await refreshPushSubs();
      showToast({
        title: 'Push enabled',
        body: 'You should now receive desktop notifications on this device.',
        variant: 'success',
      });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[push] enrollment failed', err);
      const msg = err?.message || 'Could not enable push notifications.';
      setPushError(msg);
      showToast({
        title: "Couldn't enable push notifications",
        body: msg,
        variant: 'danger',
      });
    } finally {
      setPushBusy(false);
    }
  }, [adminApi, pushSupported, refreshPushSubs, showToast, vapidStatus.key]);

  const handleRemovePushSub = useCallback(
    async (subscriptionId) => {
      try {
        await operatorUnsubscribePush(adminApi, subscriptionId);
        // Also clear the browser-side sub if it's THIS browser —
        // matching by endpoint would be more precise but the API
        // only returns subscription_id, so we settle for "this
        // browser's sub if any" via the SW registration.
        try {
          const reg = await navigator.serviceWorker.ready;
          const browserSub = await reg.pushManager.getSubscription();
          if (browserSub) await browserSub.unsubscribe();
        } catch {
          /* swallow — server side already gone */
        }
        await refreshPushSubs();
        showToast({
          title: 'Device removed',
          variant: 'success',
        });
      } catch (err) {
        showToast({
          title: "Couldn't remove that device",
          body: err?.message || 'The backend rejected the unsubscribe request.',
          variant: 'danger',
        });
      }
    },
    [adminApi, refreshPushSubs, showToast]
  );

  const handleToggleDnd = useCallback(
    async (subscriptionId, value) => {
      try {
        await setPushDoNotDisturb(adminApi, subscriptionId, value);
        await refreshPushSubs();
      } catch (err) {
        showToast({
          title: "Couldn't update Do Not Disturb",
          body: err?.message || 'The backend rejected the change.',
          variant: 'danger',
        });
      }
    },
    [adminApi, refreshPushSubs, showToast]
  );

  const handleSendTestPush = useCallback(async () => {
    setPushTestBusy(true);
    setPushError(null);
    try {
      const result = await sendPushTest(adminApi);
      const sent = result?.sent || 0;
      // eslint-disable-next-line no-console
      console.log('[push] backend fan-out reported', sent, 'devices');
      if (sent > 0) {
        showToast({
          title: 'Test push sent',
          body: `Fan-out hit ${sent} device${sent === 1 ? '' : 's'}.`,
          variant: 'success',
        });
      } else {
        showToast({
          title: 'No devices enrolled',
          body: 'Enable push on this device first.',
          variant: 'info',
        });
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[push] backend test failed', err);
      showToast({
        title: "Couldn't send a test notification",
        body: err?.message || 'The backend rejected the test request.',
        variant: 'danger',
      });
    } finally {
      setPushTestBusy(false);
    }
  }, [adminApi, showToast]);

  /** Browser-only test — fires ``new Notification`` straight from
   *  the page without going through the SW or backend.  Lets the
   *  user prove the OS-level permission path works independently
   *  of our push plumbing, which is the most common cause of
   *  "I clicked Send test and saw nothing" reports.
   */
  const handleSendLocalTest = useCallback(async () => {
    if (!pushSupported) return;
    setLocalTestBusy(true);
    setPushError(null);
    try {
      let permission = Notification.permission;
      if (permission === 'default') {
        permission = await Notification.requestPermission();
        setPushPermission(permission);
      }
      if (permission !== 'granted') {
        const msg =
          permission === 'denied'
            ? 'Notifications are blocked in your browser settings.'
            : 'Permission needed before we can show a notification.';
        setPushError(msg);
        return;
      }
      // Prefer the SW route so the OS treats it identically to a
      // real push (icon, badge, click handler).  Fall back to the
      // page-scoped Notification constructor if the SW isn't
      // controlling the page yet.
      try {
        const reg = await navigator.serviceWorker.ready;
        await reg.showNotification('Spotlight test (local)', {
          body: 'If you see this, your OS notifications work. No backend involved.',
          icon: '/icons/icon-192.png',
          badge: '/icons/icon-192.png',
          tag: 'spotlight-local-test',
        });
      } catch {
        // eslint-disable-next-line no-new
        new Notification('Spotlight test (local)', {
          body: 'If you see this, your OS notifications work. No backend involved.',
        });
      }
      showToast({
        title: 'Local test fired',
        body: 'Look for the OS notification — if you see nothing, the OS is suppressing it (Focus / Do Not Disturb / browser-level mute).',
        variant: 'info',
      });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[push] local test failed', err);
      const msg = err?.message || 'Could not show a local test notification.';
      setPushError(msg);
      showToast({
        title: "Couldn't show a local test",
        body: msg,
        variant: 'danger',
      });
    } finally {
      setLocalTestBusy(false);
    }
  }, [pushSupported, showToast]);

  const handleSave = useCallback(async () => {
    if (!profile) return;
    setSaving(true);
    try {
      const updated = await updateMyProfile(getToken, {
        display_name: profile.display_name || '',
        signature: profile.signature || '',
        timezone: profile.timezone || '',
        locale: profile.locale || '',
        pinpoint_visible: !!profile.pinpoint_visible,
      });
      setProfile(updated);
      setOriginal(updated);
      showToast({ title: 'Profile saved', variant: 'success' });
    } catch (err) {
      showToast({
        title: "Couldn't save your profile",
        body: err?.message || 'The backend rejected the update.',
        variant: 'danger',
      });
    } finally {
      setSaving(false);
    }
  }, [profile, getToken, showToast]);

  const handleAvatarPick = useCallback(
    async (file) => {
      if (!file) return;
      if (!AVATAR_MIMES.has(file.type)) {
        showToast({
          title: 'Unsupported image format',
          body: 'Avatar must be a PNG, JPEG, GIF, or WEBP image.',
          variant: 'warning',
        });
        return;
      }
      if (file.size > AVATAR_MAX_BYTES) {
        showToast({
          title: 'Image too large',
          body: 'Avatar must be under 2 MB.',
          variant: 'warning',
        });
        return;
      }
      setUploading(true);
      try {
        const result = await uploadAvatar(getToken, file);
        // Refresh the profile so the new avatar_url comes back with
        // the freshly-signed display URL in tow.
        await load();
        showToast({ title: 'Avatar updated', variant: 'success' });
        return result;
      } catch (err) {
        showToast({
          title: "Couldn't upload that avatar",
          body: err?.message || 'The backend rejected the upload.',
          variant: 'danger',
        });
      } finally {
        setUploading(false);
      }
      return undefined;
    },
    [getToken, load, showToast]
  );

  const handleDrop = useCallback(
    (event) => {
      event.preventDefault();
      event.stopPropagation();
      const file = event.dataTransfer?.files?.[0];
      if (file) handleAvatarPick(file);
    },
    [handleAvatarPick]
  );

  const handleSignOutEverywhere = useCallback(async () => {
    const confirmed = await askConfirm({
      title: 'Sign out of every device?',
      body: 'You’ll need to sign in again everywhere you’re currently using the dashboard. Other browser windows will lose access immediately.',
      confirmLabel: 'Sign out everywhere',
      danger: true,
    });
    if (!confirmed) return;
    try {
      // ``signOut()`` on the Clerk client revokes every active
      // session token for this user — not just the current tab.
      // Verified against @clerk/clerk-react v5.x docs.
      await clerk.signOut();
    } catch (err) {
      showToast({
        title: "Couldn't sign out everywhere",
        body: err?.message || 'Your sign-in provider rejected the request.',
        variant: 'danger',
      });
    }
  }, [askConfirm, clerk, showToast]);

  if (loading) {
    return (
      <div className="page">
        <div className="page__title">
          <h1>Your profile</h1>
        </div>
        <div className="state">
          <div className="state__title">Loading…</div>
        </div>
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="page">
        <div className="page__title">
          <h1>Your profile</h1>
        </div>
        <div className="state state--error">
          <div className="state__title">Couldn’t load your profile</div>
          <div>{error}</div>
          <button className="btn" onClick={load}>
            <RefreshIcon /> Try again
          </button>
        </div>
      </div>
    );
  }

  const avatarDisplaySrc = profile.avatar_url
    ? signedAvatarSrc(profile.avatar_url)
    : profile.idp_avatar_url || '';

  return (
    <div className="page">
      <div className="page__title">
        <div>
          <h1>Your profile</h1>
          <p className="page__subtitle">
            How you appear in the dashboard and in Pinpoint replies. Changes
            sync to every tenant you belong to.
          </p>
        </div>
        <div className="page__actions">
          <button
            className="btn btn--primary"
            disabled={!isDirty || saving}
            onClick={handleSave}
          >
            <SaveIcon /> {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>

      <section className="card profile-card">
        <h2 className="profile-card__title">Identity</h2>

        <div
          className="profile-avatar"
          ref={dropTargetRef}
          onDragOver={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          onDrop={handleDrop}
        >
          {avatarDisplaySrc ? (
            <img
              className="profile-avatar__img"
              src={avatarDisplaySrc}
              alt="Your avatar"
            />
          ) : (
            <div className="profile-avatar__placeholder">
              {(profile.display_name || profile.idp_display_name || '?')
                .charAt(0)
                .toUpperCase()}
            </div>
          )}
          <div className="profile-avatar__controls">
            <button
              type="button"
              className="btn"
              disabled={uploading}
              onClick={() => fileInputRef.current?.click()}
            >
              <UploadIcon /> {uploading ? 'Uploading…' : 'Upload new avatar'}
            </button>
            <p className="muted small">
              Drop an image here, or pick a PNG, JPEG, GIF, or WEBP under 2 MB.
              SVG is not allowed.
            </p>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg,image/gif,image/webp"
            style={{ display: 'none' }}
            onChange={(e) => handleAvatarPick(e.target.files?.[0])}
          />
        </div>

        <label className="field">
          <span className="field__label">Display name</span>
          <input
            type="text"
            value={profile.display_name || ''}
            maxLength={DISPLAY_NAME_MAX}
            placeholder={profile.idp_display_name || 'Your name'}
            onChange={(e) =>
              setProfile({ ...profile, display_name: e.target.value })
            }
          />
          <span className="field__hint">
            Shown above your messages in Pinpoint. Leave blank to use the name
            from your sign-in provider ({profile.idp_display_name || 'unset'}).
          </span>
        </label>

        <label className="field">
          <span className="field__label">Email</span>
          <input
            type="email"
            value={profile.email || ''}
            readOnly
            disabled
          />
          <span className="field__hint">
            Managed by your sign-in provider. Change it there to update it
            here.
          </span>
        </label>

        <label className="field">
          <span className="field__label">Reply signature</span>
          <textarea
            value={profile.signature || ''}
            maxLength={SIGNATURE_MAX}
            rows={4}
            placeholder="— Sam, Customer Support"
            onChange={(e) =>
              setProfile({ ...profile, signature: e.target.value })
            }
          />
          <span className="field__hint">
            Appended to outgoing Pinpoint replies. Plain text only.
          </span>
        </label>

        <label className="field field--inline">
          <input
            type="checkbox"
            checked={!!profile.pinpoint_visible}
            onChange={(e) =>
              setProfile({ ...profile, pinpoint_visible: e.target.checked })
            }
          />
          <span>Show my name and avatar in Pinpoint reply bubbles</span>
        </label>
      </section>

      <section className="card profile-card">
        <h2 className="profile-card__title">Locale &amp; timezone</h2>
        <label className="field">
          <span className="field__label">Timezone</span>
          <select
            value={profile.timezone || ''}
            onChange={(e) =>
              setProfile({ ...profile, timezone: e.target.value })
            }
          >
            <option value="">Inherit from this browser</option>
            {TIMEZONES.map((tz) => (
              <option key={tz} value={tz}>
                {tz}
              </option>
            ))}
          </select>
          <span className="field__hint">
            Affects how timestamps render across the dashboard.
          </span>
        </label>

        <label className="field">
          <span className="field__label">Language</span>
          <select
            value={profile.locale || ''}
            onChange={(e) =>
              setProfile({ ...profile, locale: e.target.value })
            }
          >
            {LOCALES.map((l) => (
              <option key={l.code} value={l.code}>
                {l.label}
              </option>
            ))}
          </select>
        </label>
      </section>

      {/* Notification preferences — card spotlight-047 */}
      <section className="card profile-card">
        <h2 className="profile-card__title">Desktop notifications</h2>
        <p className="muted">
          Get a desktop pop when a new help conversation opens on a project
          you admin. Works in the background; click the notification to jump
          straight to the thread.
        </p>

        {!pushSupported ? (
          <div className="state state--muted">
            <div className="state__title">Not supported here</div>
            <div>
              This browser doesn’t expose the Web Push APIs. Try a recent
              Chrome, Firefox, or Edge — Safari on macOS 13+ also works.
            </div>
          </div>
        ) : (
          <>
            <div className="profile-push__status">
              <span
                className={`profile-push__dot profile-push__dot--${
                  pushPermission === 'granted'
                    ? 'on'
                    : pushPermission === 'denied'
                      ? 'blocked'
                      : 'idle'
                }`}
              />
              <span>
                Permission:{' '}
                {pushPermission === 'granted'
                  ? 'granted'
                  : pushPermission === 'denied'
                    ? 'blocked — change in browser settings'
                    : 'not requested yet'}
              </span>
            </div>

            {vapidStatus.checked && vapidStatus.error && (
              <div className="profile-push__callout">
                <strong>Push isn’t configured for this environment.</strong>{' '}
                Ask an admin to seed the VAPID keys in SSM. Details:{' '}
                {vapidStatus.error}
              </div>
            )}

            <div className="profile-push__actions">
              <button
                type="button"
                className="btn btn--primary"
                disabled={
                  pushBusy ||
                  pushPermission === 'denied' ||
                  (vapidStatus.checked && !!vapidStatus.error)
                }
                onClick={handleEnablePush}
              >
                {pushBusy
                  ? 'Enabling…'
                  : pushPermission === 'granted'
                    ? 'Re-enable on this device'
                    : 'Enable desktop notifications'}
              </button>
              <button
                type="button"
                className="btn"
                disabled={localTestBusy || pushPermission === 'denied'}
                onClick={handleSendLocalTest}
                title="Fires a browser-only notification — bypasses the backend."
              >
                {localTestBusy ? 'Showing…' : 'Send local test'}
              </button>
              <button
                type="button"
                className="btn"
                disabled={pushTestBusy || pushSubs.length === 0}
                onClick={handleSendTestPush}
                title={
                  pushSubs.length === 0
                    ? 'Enable push first to send a backend test'
                    : 'Fires a real push via the backend fan-out path.'
                }
              >
                {pushTestBusy ? 'Sending…' : 'Send push test'}
              </button>
            </div>

            {pushError && (
              <div className="profile-push__error" role="alert">
                {pushError}
              </div>
            )}

            <h3 className="profile-push__subhead">Your enrolled devices</h3>
            {pushSubs.length === 0 ? (
              <div className="state state--muted">
                <div>No devices yet — enable push above.</div>
              </div>
            ) : (
              <ul className="profile-push__list">
                {pushSubs.map((sub) => (
                  <li key={sub.subscription_id} className="profile-push__row">
                    <div className="profile-push__row-main">
                      <div className="profile-push__row-title">
                        {parseUserAgent(sub.user_agent)}
                      </div>
                      <div className="profile-push__row-meta muted small">
                        Last active{' '}
                        {sub.last_seen_at
                          ? new Date(sub.last_seen_at).toLocaleString()
                          : 'just now'}
                      </div>
                    </div>
                    <label className="profile-push__row-dnd">
                      <input
                        type="checkbox"
                        checked={!!sub.do_not_disturb}
                        onChange={(e) =>
                          handleToggleDnd(
                            sub.subscription_id,
                            e.target.checked
                          )
                        }
                      />
                      <span>Do not disturb</span>
                    </label>
                    <button
                      type="button"
                      className="btn btn--small"
                      onClick={() => handleRemovePushSub(sub.subscription_id)}
                    >
                      Remove
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </section>

      <section className="card profile-card">
        <h2 className="profile-card__title">Sessions</h2>
        <p className="muted">
          End every active session on every device. You’ll be asked to sign in
          again the next time you open the dashboard.
        </p>
        <button
          type="button"
          className="btn btn--danger"
          onClick={handleSignOutEverywhere}
        >
          <DisableIcon /> Sign out everywhere
        </button>
      </section>

      {confirmNode}

      <style>{`
        .profile-card { margin-bottom: 16px; padding: 20px; }
        .profile-card__title {
          margin: 0 0 12px;
          font-size: 16px;
          font-weight: 600;
        }
        .profile-avatar {
          display: flex;
          gap: 16px;
          align-items: center;
          padding: 12px;
          border: 1px dashed var(--border-2, #d0d4dc);
          border-radius: var(--r-md, 8px);
          margin-bottom: 16px;
        }
        .profile-avatar__img,
        .profile-avatar__placeholder {
          width: 72px;
          height: 72px;
          border-radius: 50%;
          object-fit: cover;
        }
        .profile-avatar__placeholder {
          display: grid;
          place-items: center;
          background: var(--surface-2, #ebeef3);
          font-size: 28px;
          font-weight: 600;
        }
        .profile-avatar__controls { display: flex; flex-direction: column; gap: 6px; }
        .field { display: block; margin-bottom: 14px; }
        .field--inline {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 8px;
        }
        .field__label {
          display: block;
          font-weight: 600;
          margin-bottom: 4px;
          font-size: 13px;
        }
        .field input[type="text"],
        .field input[type="email"],
        .field textarea,
        .field select {
          width: 100%;
          max-width: 480px;
          padding: 8px 10px;
          border: 1px solid var(--border-1, #c7ccd6);
          border-radius: var(--r-sm, 6px);
        }
        .field__hint {
          display: block;
          color: var(--text-3, #555);
          font-size: 12px;
          margin-top: 4px;
        }
        .muted { color: var(--text-3, #555); }
        .small { font-size: 12px; }
        /* Push notification preferences — card 047 */
        .profile-push__status {
          display: flex;
          align-items: center;
          gap: 8px;
          margin: 12px 0;
          font-size: 13px;
        }
        .profile-push__dot {
          width: 10px;
          height: 10px;
          border-radius: 50%;
        }
        .profile-push__dot--on { background: var(--success-500, #16a34a); }
        .profile-push__dot--blocked { background: var(--danger-500, #dc2626); }
        .profile-push__dot--idle { background: var(--warn-500, #f59e0b); }
        .profile-push__actions {
          display: flex;
          gap: 10px;
          flex-wrap: wrap;
          margin-bottom: 12px;
        }
        .profile-push__subhead {
          margin: 16px 0 8px;
          font-size: 13px;
          font-weight: 600;
        }
        .profile-push__list {
          list-style: none;
          padding: 0;
          margin: 0;
          display: flex;
          flex-direction: column;
          gap: 8px;
        }
        .profile-push__row {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 10px 12px;
          border: 1px solid var(--border-1, #c7ccd6);
          border-radius: var(--r-md, 8px);
        }
        .profile-push__row-main { flex: 1; min-width: 0; }
        .profile-push__row-title { font-weight: 500; font-size: 13px; }
        .profile-push__row-dnd {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 12px;
          white-space: nowrap;
        }
        .profile-push__callout {
          margin: 12px 0;
          padding: 10px 12px;
          background: var(--warn-50, #fffbeb);
          border: 1px solid var(--warn-200, #fde68a);
          color: var(--warn-900, #78350f);
          border-radius: var(--r-md, 8px);
          font-size: 13px;
        }
        .profile-push__error {
          margin-top: 4px;
          padding: 8px 12px;
          background: var(--danger-50, #fef2f2);
          border: 1px solid var(--danger-200, #fecaca);
          color: var(--danger-800, #991b1b);
          border-radius: var(--r-md, 8px);
          font-size: 13px;
        }
      `}</style>
    </div>
  );
}
